import "@animated-burgers/burger-slip/dist/styles.css";
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { config, animated, useSpring } from "react-spring";
import Burger from "@animated-burgers/burger-slip";
import { css } from "emotion";

import Container from "components/shared/Container";

import { routes } from "config";
import styleConfig from "styles/styleConfig";

const { margin, padding, colors, gradient, boxShadow, borderRadius, transition, screens } = styleConfig;
const height = "50px";
const triggerNav = 250;

const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgba(25, 25, 25, 0.35);
  z-index: 91;
`;

const navStyle = css`
  height: ${height};
  margin: 170px 0 calc(50px + 1rem);
  background: ${gradient.primary};
  .container {
    nav {
      position: relative;
      height: ${height};
      margin-top: 170px;
      &.fixed {
        position: fixed;
        left: 0;
        animation: comeIn;
        animation-duration: 0.45s;
        animation-timing-function: ease;
        animation-fill-mode: both;
        justify-content: flex-end;
        .logo {
          position: relative;
          width: 80px;
          top: -137px;
          right: initial;
          padding: ${padding[1]};
          margin-left: ${margin[2]};
        }
        .nav-content {
          justify-content: flex-end;
          width: 1140px;
          margin: 0 auto;
        }
      }
      .nav-content {
        display: flex;
        width: 100%;
        justify-content: space-around;
      }
      .logo {
        position: absolute;
        top: -307px;
        left: 0;
        right: 0;
        margin: 0 auto;
        padding: ${padding[4]};
        width: 256px;
        background: ${colors.white};
        box-shadow: ${boxShadow.default};
        border-radius: 0 0 ${borderRadius.md} ${borderRadius.md};
        .img {
        }
        z-index: 90;
        margin-top: 137px;
        /* @media (max-width: ${screens.MOBILE_WIDTH}px) {
          right: initial;
          padding: ${padding[2]};
        } */
      }
      .right,
      .left {
      }
      ul {
        list-style: none;
        display: flex;
        text-align: center;
        li {
          width: 100px;
          a {
            display: block;
            color: ${colors.gray[100]};
            line-height: ${height};
            font-weight: bolder;
            text-transform: uppercase;
            text-decoration: none;
            transition: ${transition.default};
            &:hover {
              color: ${colors.gray[300]};
            }
            &:active {
              color: ${colors.gray[400]};
            }
          }
        }
      }
      /* BURGER NAV */
      .burger {
        display: none;
      }
      .nav-hamburger-sidebar {
        display: none;
      }
      @media (max-width: ${screens.MOBILE_WIDTH}px) {
        .burger {
          display: block;
        }
        .right,
        .left {
          display: none;
        }
        .nav-hamburger-sidebar {
          display: block;
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          background: ${colors.primary[400]};
          z-index: 100;
          width: 250px;
          .content {
            margin-top: 260px;
            ul {
              display: flex;
              flex-direction: column;
              text-align: left;
              padding: 0 ${padding[8]};
            }
          }
        }
      }
    }
  }

  @keyframes comeIn {
    0% {
      opacity: 0;
      margin-top: -254px;
    }
    100% {
      margin-top: -204px;
      opacity: 1;
      width: 100%;
      background: ${gradient.primary};
      z-index: 999;
    }
  }
`;

const burgerStyle = css`
  position: absolute;
  top: 4px;
  right: ${margin[4]};
  z-index: 110;
`;

interface Props {}

const Nav: React.FC<Props> = () => {
  const ref = useRef<null | any>(null);
  const [isSticky, setSticky] = useState(false);
  const [burgerIsOpen, setBurgerIsOpen] = useState(false);
  const { t } = useTranslation();
  const logoAnimation = useSpring({
    from: { opacity: 0, transform: "translateY(-30px) scale(0.5)" },
    to: { opacity: 1, transform: "translateY(0px) scale(1)" },
    config: config.gentle,
  });
  const sidebarAnimation = useSpring({
    opacity: burgerIsOpen ? 1 : 0.5,
    transform: burgerIsOpen ? "translate3d(2rem, 0, 0)" : "translate3d(18rem, 0, 0)",
    config: config.gentle,
  });

  const handleScroll = () => {
    const isTop = window.scrollY > triggerNav;
    if (isTop) setSticky(true);
    else setSticky(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", () => handleScroll);
    };
  }, []);

  return (
    <div className={navStyle}>
      <Container className="container">
        <nav ref={ref} className={`${isSticky ? "fixed" : ""}`}>
          <div className="nav-content">
            <animated.div className="logo" style={logoAnimation}>
              <Link to="/">
                <img src="./assets/erba_square_logo.svg" alt="erba-supermarkt" />
              </Link>
            </animated.div>
            <Burger
              direction="right"
              isOpen={burgerIsOpen}
              onClick={() => setBurgerIsOpen(!burgerIsOpen)}
              className={burgerStyle}
            />
            <animated.div className={`nav-hamburger-sidebar${burgerIsOpen ? " openn" : ""}`} style={sidebarAnimation}>
              <div className="content">
                <ul>
                  {routes.map(({ name, path }) => (
                    <li key={name}>
                      <Link to={path}>
                        <span onClick={() => setBurgerIsOpen(false)}>{t(`nav.${name}`)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </animated.div>
            {burgerIsOpen && <div className={overlayStyle} onClick={() => setBurgerIsOpen(false)} />}
            <div className="left">
              <ul>
                {[routes[0], routes[1]].map(({ name, path }) => (
                  <li key={name}>
                    <Link to={path}>{t(`nav.${name}`)}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="right">
              <ul>
                {[routes[2], routes[3]].map(({ name, path }) => (
                  <li key={name}>
                    <Link to={path}>{t(`nav.${name}`)}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </nav>
      </Container>
    </div>
  );
};

export default Nav;
